/**
 * Keeps the PocketBase session alive without ever losing what the user was
 * doing.
 *
 * PocketBase cannot renew a token once it has expired (auth-refresh itself
 * requires a valid token), so there are two layers:
 *
 *   1. keepAlive(): while the token is still valid, renew it well before it
 *      expires. An app left open in a tab or in the Dock never hits expiry.
 *   2. waitForReauth(): if a call fails with 401 anyway, the API client parks
 *      it here. The app shows a sign-in dialog ON TOP of the current page, so
 *      the form being submitted stays mounted; once the user has signed in
 *      again, every parked call is replayed with the new token.
 *
 * Signing out the old way (clearing the store) unmounted the page and threw
 * the half-sent form away — this module exists to avoid exactly that.
 */
import { pb } from './pocketbase';

// Renew once less than this is left (tokens last 7 days on this server).
const RENEW_BELOW_MS = 2 * 24 * 60 * 60 * 1000;

export function tokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

let renewing = null;

/** Renew the token right now. Concurrent callers share one request. */
export async function renewNow() {
  renewing = renewing || pb.collection('users').authRefresh();
  try {
    await renewing;
  } finally {
    renewing = null;
  }
}

/** Renew the token if it is still valid but close to expiry. Never throws. */
export async function keepAlive() {
  const token = pb.authStore.token;
  if (!token || !pb.authStore.isValid) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const exp = tokenExpiry(token);
  if (exp && exp - Date.now() > RENEW_BELOW_MS) return;
  try {
    await renewNow();
  } catch {
    // A rejected renewal is handled by the 401 path on the next API call.
  }
}

// ---------- Re-authentication bridge (API client ⇄ AuthContext) ----------

let pending = null; // { promise, resolve, reject }
const listeners = new Set();

function notify() {
  listeners.forEach((fn) => fn(Boolean(pending)));
}

/** Subscribe to "a sign-in is needed" changes. Returns an unsubscribe. */
export function onReauthChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Resolves once the user has signed in again; rejects if they give up
 * (sign out instead). Concurrent callers share the same dialog.
 */
export function waitForReauth() {
  if (!pending) {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    pending = { promise, resolve, reject };
    notify();
  }
  return pending.promise;
}

export function completeReauth() {
  if (!pending) return;
  const { resolve } = pending;
  pending = null;
  notify();
  resolve();
}

export function abandonReauth() {
  if (!pending) return;
  const { reject } = pending;
  pending = null;
  notify();
  reject(new Error('reauth_abandoned'));
}

/**
 * Response interceptor for an axios instance that sends the PocketBase token.
 *
 * An expired token used to fail every call with a silent 401: the admin page
 * showed "no presets", buttons did nothing, a new lead ended in "An error
 * occurred". Renew it once and replay the call. If PocketBase refuses the
 * renewal (the token has already expired), park the call until the user has
 * signed in again in the dialog shown over the page, then replay it: signing
 * out here would unmount the page and lose the form being sent.
 */
export function installSessionRecovery(instance) {
  instance.interceptors.response.use(undefined, async (error) => {
    const { config, response } = error;
    if (response?.status !== 401 || !config || config._authRetried || !pb.authStore.token) {
      throw error;
    }
    config._authRetried = true;
    try {
      await renewNow();
    } catch (refreshError) {
      // A network hiccup keeps the session and surfaces the original error.
      if (!(refreshError?.status >= 400 && refreshError?.status < 500)) throw error;
      try {
        await waitForReauth();
      } catch {
        throw error; // the user chose to sign out instead
      }
    }
    config.headers.Authorization = `Bearer ${pb.authStore.token}`;
    return instance(config);
  });
}

/** The email of the account whose session expired, to prefill the dialog. */
export function currentEmail() {
  const record = pb.authStore.record || pb.authStore.model;
  return record?.email || '';
}
